import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { getAuthInstance } from './client.js';
import { doitFrapper, orgDuRattachement, rattacherCompteCourant, signalerConnexionCourante } from './rattachement.js';
import type { Rattachement } from './rattachement.js';
import type { TakiClaims, UseUserResult } from './types.js';

const EMPTY_CLAIMS: TakiClaims = {};

/**
 * Extrait les claims MyTaki (orgId, orgRole) depuis le token d'un utilisateur.
 * `getIdTokenResult()` déclenche un rafraîchissement si nécessaire et expose `claims`.
 */
async function readClaims(user: User): Promise<TakiClaims> {
  const tokenResult = await user.getIdTokenResult();
  const raw = tokenResult.claims;
  const orgId = typeof raw.orgId === 'string' ? raw.orgId : undefined;
  const orgRole = typeof raw.orgRole === 'string' ? raw.orgRole : undefined;
  return { orgId, orgRole };
}

/**
 * Hook React donnant l'utilisateur courant, ses claims (orgId, orgRole), le
 * verdict de la porte et l'état de chargement.
 *
 * - S'abonne à `onAuthStateChanged` pour suivre les connexions/déconnexions.
 * - À chaque utilisateur connecté, lit `getIdTokenResult().claims`.
 * - LA PORTE : si le jeton ne porte pas l'organisation de cette page, la
 *   question « cette adresse est-elle invitée ici ? » part au serveur (voir
 *   `rattachement.ts`), et les claims sont relus après. C'est le seul chemin
 *   par lequel un compte tout neuf (Google, ou un mot de passe créé depuis
 *   l'écran des utilisateurs) obtient ses droits.
 * - `loading` reste `true` jusqu'à résolution de TOUT ça : un écran monté entre
 *   les deux lirait des claims vides et croirait à un refus.
 *
 * Nécessite que `initAuth(app)` ait été appelé au préalable. Si ce n'est pas le cas,
 * le hook reste en état déconnecté (`user: null`, `loading: false`).
 */
export function useUser(): UseUserResult {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<TakiClaims>(EMPTY_CLAIMS);
  const [rattachement, setRattachement] = useState<Rattachement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const auth = getAuthInstance();
    if (!auth) {
      // initAuth pas encore appelé : état déconnecté stable.
      setUser(null);
      setClaims(EMPTY_CLAIMS);
      setRattachement(null);
      setLoading(false);
      return;
    }

    let active = true;

    /**
     * Résout un utilisateur : ses claims, et la porte quand elle est due.
     *
     * L'ordre compte. On lit les claims AVANT de décider de frapper, parce que
     * c'est le jeton qui dit si la question est déjà tranchée ; et on les relit
     * APRÈS un rattachement accordé, parce que `rattacherCompteCourant` vient
     * alors de rafraîchir le jeton et que l'ancien ne portait rien.
     */
    async function resoudre(nextUser: User): Promise<void> {
      let prochainsClaims = await readClaims(nextUser).catch(() => EMPTY_CLAIMS);
      let verdict: Rattachement | null = null;

      if (doitFrapper(orgDuRattachement(), prochainsClaims.orgId)) {
        verdict = await rattacherCompteCourant(nextUser);
        if (verdict?.statut === 'ok') {
          prochainsClaims = await readClaims(nextUser).catch(() => EMPTY_CLAIMS);
        }
      }

      // LE JOURNAL : la connexion est signalee une fois les droits etablis, sans
      // l'attendre (`signalerConnexionCourante` n'echoue jamais).
      if (prochainsClaims.orgId !== undefined && prochainsClaims.orgId === orgDuRattachement()) {
        void signalerConnexionCourante(nextUser);
      }

      if (!active) return;
      setClaims(prochainsClaims);
      setRattachement(verdict);
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      if (!active) return;

      if (!nextUser) {
        setUser(null);
        setClaims(EMPTY_CLAIMS);
        setRattachement(null);
        setLoading(false);
        return;
      }

      setUser(nextUser);
      setLoading(true);

      void resoudre(nextUser)
        .catch(() => {
          if (!active) return;
          setClaims(EMPTY_CLAIMS);
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return { user, claims, rattachement, loading };
}
